import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatBRL, formatInteger } from "../../config/admin";

const REVENUE_COLOR = "#072959";
const SALES_COLOR = "#1d8a80";

function formatAxisDate(ymd, grain) {
  if (!ymd) return "";
  const [year, month, day] = String(ymd).split("-");
  if (grain === "month") {
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
  }
  return `${day}/${month}`;
}

function formatTooltipDate(ymd, grain) {
  if (!ymd) return "";
  const [year, month, day] = String(ymd).split("-").map(Number);
  const date = new Date(year, month - 1, day || 1);
  if (grain === "month") {
    return date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  }
  if (grain === "week") {
    return `Semana de ${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  }
  return date.toLocaleDateString("pt-BR");
}

function formatCompactBRL(value) {
  const amount = Number(value) || 0;
  if (amount >= 1000000) {
    return `R$ ${(amount / 1000000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })} mi`;
  }
  if (amount >= 1000) {
    return `R$ ${(amount / 1000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })} mil`;
  }
  return `R$ ${amount.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
}

function ChartTooltip({ active, payload, label, grain }) {
  if (!active || !payload?.length) return null;
  const revenue = payload.find((item) => item.dataKey === "revenue")?.value ?? 0;
  const sales = payload.find((item) => item.dataKey === "sales_count")?.value ?? 0;
  return (
    <div className="admin-chart-tooltip">
      <p className="admin-chart-tooltip-date">{formatTooltipDate(label, grain)}</p>
      <p className="admin-chart-tooltip-row">
        <span className="admin-chart-swatch" style={{ background: REVENUE_COLOR }} />
        Receita <strong>{formatBRL(revenue)}</strong>
      </p>
      <p className="admin-chart-tooltip-row">
        <span className="admin-chart-swatch" style={{ background: SALES_COLOR }} />
        Vendas <strong>{formatInteger(sales)}</strong>
      </p>
    </div>
  );
}

export default function AdminStatsChart({ points = [], grain = "day" }) {
  const data = points.map((point) => ({
    date: point.date,
    sales_count: Number(point.sales_count) || 0,
    revenue: Number(point.revenue) || 0,
  }));
  const hasValues = data.some((point) => point.sales_count > 0 || point.revenue > 0);

  if (!data.length || !hasValues) {
    return (
      <div className="admin-empty">Nenhuma venda confirmada neste período.</div>
    );
  }

  return (
    <div className="admin-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="adminRevenueFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={REVENUE_COLOR} stopOpacity={0.28} />
              <stop offset="100%" stopColor={REVENUE_COLOR} stopOpacity={0.04} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#e6eef6" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(value) => formatAxisDate(value, grain)}
            tick={{ fill: "#4a5b74", fontSize: 12 }}
            axisLine={{ stroke: "#d9e7f3" }}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            yAxisId="revenue"
            tickFormatter={formatCompactBRL}
            tick={{ fill: "#4a5b74", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={72}
          />
          <YAxis
            yAxisId="sales"
            orientation="right"
            allowDecimals={false}
            tickFormatter={formatInteger}
            tick={{ fill: "#4a5b74", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={40}
          />
          <Tooltip
            content={<ChartTooltip grain={grain} />}
            cursor={{ fill: "rgba(7, 41, 89, 0.04)" }}
          />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="circle"
            wrapperStyle={{ paddingBottom: 12, fontSize: 13, color: "#072959" }}
          />
          <Area
            yAxisId="revenue"
            type="monotone"
            dataKey="revenue"
            name="Receita"
            stroke={REVENUE_COLOR}
            strokeWidth={2.5}
            fill="url(#adminRevenueFill)"
            dot={false}
            activeDot={{ r: 5, strokeWidth: 0 }}
          />
          <Bar
            yAxisId="sales"
            dataKey="sales_count"
            name="Vendas confirmadas"
            fill={SALES_COLOR}
            radius={[6, 6, 0, 0]}
            maxBarSize={28}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
